import { computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * MYAC-883 — card chrome follows the CONVERSATION language, not the app locale.
 *
 * The chatbot detects the language of the user's latest message (sticky across
 * ambiguous turns) and stamps it on card payloads; the model's prose follows the
 * same source. Rendering the card chrome from the same stamp keeps text and
 * cards coherent: user writes English → everything English, French → everything
 * French. No stamp → the app locale, exactly the previous behavior.
 */
export function useCardLocale(language: Ref<string | undefined | null>) {
    const { t } = useI18n()
    const cardLocale = computed(() => {
        if (language.value === 'french') return 'fr'
        if (language.value === 'english') return 'en'
        return null
    })
    // The same second-argument shapes t() accepts: named params, or a plural count.
    // `plural` picks the form of a plural message whose params are named — the
    // count sits in the params AND decides the branch (MYAC-1034), which the
    // two-argument shapes cannot express on their own.
    // typeof-narrowed per branch — t()'s overloads reject the union directly.
    function ct(key: string, arg?: Record<string, unknown> | number, plural?: number): string {
        const options: { locale?: string; plural?: number } = {}
        if (cardLocale.value) options.locale = cardLocale.value
        if (plural !== undefined) options.plural = plural
        if (typeof arg === 'number') return t(key, arg, options)
        return t(key, arg ?? {}, options)
    }
    return { ct, cardLocale }
}
